import { useHistory } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { Box, Collapse, Grid, IconButton, Skeleton as MuiSkeleton, Stack, Tooltip, Typography } from '@mui/material';

import Details from 'Lib/components/page/Details';
import isFunction from 'Lib/utilities/isFunction';
import { screenSize } from 'Lib/utilities/screenSize';

import Card from './Card';

const Skeleton = () => {
	return Array(12)
		.fill({})
		.map((__, index) => (
			<Box
				key={index}
				mb={2}
				p={4}
				display="flex"
				flexDirection="column"
				gap={2}
				sx={theme => ({
					borderRadius: theme.spacing(3),
					border: `1px solid ${theme.palette.common.lightgray}`,
					bgcolor: 'common.lightblue'
				})}>
				<MuiSkeleton sx={{ fontSize: '16px', width: '10rem' }} />
				<MuiSkeleton sx={{ fontSize: '16px', width: '100%' }} />
				<MuiSkeleton sx={{ fontSize: '16px', width: '20rem' }} />
			</Box>
		));
};

const DataCard = ({ data, renderDetails, index }) => {
	const { mobile, tablet } = screenSize();
	const history = useHistory();

	const { body, header, subheader, title, actions, detailsLink } = renderDetails(data, index);
	const emptySubheader = subheader?.props?.children?.type?.name === 'TableCardSubheader';

	const [open, setOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const handleClick = () => {
		if (detailsLink) history.push(detailsLink);
		else setOpen(true);
	};

	const DetailsContainer = () => (
		<Details open={open} title={title} actions={actions} toggle={() => setOpen(false)}>
			{body}
		</Details>
	);

	const DetailsButton = () => {
		if (!tablet && mobile) {
			return <IconButton onClick={handleClick}>keyboard_arrow_right</IconButton>;
		} else {
			return (
				<Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow placement="top">
					<div>
						<IconButton onClick={() => setExpanded(!expanded)}>{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</IconButton>
					</div>
				</Tooltip>
			);
		}
	};

	return (
		<Fragment>
			<Card sx={{ mb: 2, maxWidth: '100vw' }}>
				<Grid container sx={{ flexWrap: 'nowrap' }}>
					<Grid item xs={11} sx={{ maxWidth: 'calc(100% - 48px) !important' }}>
						<Typography component="div" noWrap sx={{ width: '100%' }}>
							{isFunction(header) ? header() : (header ?? '--')}
						</Typography>
						{(!expanded || emptySubheader) && subheader && (
							<Typography component="div" noWrap sx={{ width: '100%' }} onClick={() => detailsLink && history.push(detailsLink)}>
								{isFunction(subheader) ? subheader() : subheader}
							</Typography>
						)}
						{body && <Collapse in={expanded}>{body}</Collapse>}
					</Grid>
					<Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
						<Stack direction="column" justifyContent={!tablet && mobile ? 'center' : 'space-between'} alignItems="flex-end">
							{(body || detailsLink) && <DetailsButton />}
							{(expanded || !body) && actions}
						</Stack>
					</Grid>
				</Grid>
			</Card>
			{body && <DetailsContainer />}
		</Fragment>
	);
};

export default function TableCard({ data, loading, renderDetails }) {
	if (loading) return <Skeleton />;

	return data.map((datum, index) => {
		return <DataCard key={index} index={index} data={datum} renderDetails={renderDetails} />;
	});
}
